import { useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface InvestorLetter {
  author: string;
  title: string;
  greeting: string;
  opening?: string;
  paragraphs: (string | { type: "bullets"; items: string[] })[];
  closing?: string;
  signature?: string;
  credentials: string[];
}

interface InvestorLetterPopupProps {
  letters: InvestorLetter[];
  triggerElement: React.ReactNode;
}

export const INVESTOR_LETTERS_DATA: InvestorLetter[] = [
  {
    author: "Louie Ehrlich",
    title: "Former CIO, Chevron",
    greeting: "",
    paragraphs: [
      "My name is Louie Ehrlich, former Chief Information Officer of Chevron, and an active angel investor who has spent much of my career wrestling with the exact challenges iSPAI was built to solve — how organizations capture, structure, and apply the knowledge they generate every day – and how they incentivize employees to actively participate. Across decades of leading large, complex operations, I saw firsthand how much value is lost when institutional knowledge sits in documents, systems, and people's heads instead of being accessible, scalable, and actionable. And that is the core reason I invested in iSPAI.",
      "What makes iSPAI stand out to me is the combination of AI reasoning with blockchain governance. You get domain specific enterprise intelligence systems where knowledge is trustworthy and people are actually motivated to keep it accurate. In many ways, it's a self-reinforcing, ever improving ecosystem.",
      "I've championed and led many knowledge management initiatives that required millions of dollars, global teams, and years of effort – and they almost always fall short. The promise of AI has always been it could finally crack the code to the challenges inherent in these efforts, but most tools I've seen in this regard fall short because they are too broad and too generic to drive real business outcomes. iSPAI's approach is different. It is focused, workflow-driven and rooted in solving practical knowledge management problems that every operator, analyst, executive and company faces. It's why I view the target market for this to be substantial.",
      "In my experience as an investor, what I've found is that the capabilities, passion, and clarity of mission of the founders is at least as important as the product or service being offered and its potential market size. This was a key consideration when I made the decision to invest in iSPAI. I've known Trey Fleming for many years beginning in my role as CIO of Chevron. He has the discipline, initiative, and a level of humility that I think are critical for early-stage founders, and frankly are rare to see. Through numerous conversations and working sessions, I've been consistently impressed with Trey's clarity of thought, and deep understanding of both the technology and the business problems behind it.",
      "While each investor should conduct their own diligence, I offer my full support for iSPAI and encourage you to give them serious consideration. I believe they have the vision, the timing, and the leadership to build something impactful, especially with the right partners alongside them."
    ],
    closing: "Regards,",
    signature: "Louie Ehrlich",
    credentials: ["Former CIO, Chevron", "CEO, Ehrlich Capital LLC", "Angel Investor & Advisor"]
  },
  {
    author: "Ed Barrows",
    title: "Retired Oil & Gas CIO",
    greeting: "",
    opening: "I'm writing in support of iSPAI",
    paragraphs: [
      "My name is Ed Barrows, and I am a retired Oil & Gas CIO, plus one of the iSPAI Angel Investors.",
      "iSPAI has a novel unique approach to a long-standing issue for large enterprises and organizations. Namely, knowledge loss from talent loss due to retirees or displacement from automation. This is especially true for highly regulated asset heavy industries like O&G and Chemicals.",
      "Unlike generic AI tools, iSPAI integrates AI reasoning, Web3 identity, and peer-validated human knowledge creation into a single system. This creates:",
      {
        type: "bullets",
        items: [
          "Provenance (where knowledge came from)",
          "Attribution (who created or confirmed it)",
          "Explainability (why a recommendation is correct)",
          "Incentives (creators and contributors get rewarded)"
        ]
      },
      "From my experience at Gartner, it is rare to find an organization that can do this well, but just as importantly cost effectively without unnecessary overhead and burdensome processes. This is essential to keep subject matter experts engaged / rewarded across all roles (Creators, Composers, and Consumers).",
      "Learnings from early pilots are encouraging, and the use cases that have been explored demonstrate that iSPAI has broad applicability across industries and knowledge domains. Just as importantly, the implementation speed is impressive given that large enterprises are numb from past transformations: ERP, digitization, and cloud computing.",
      "One thing that distinguishes iSPAI leadership team is that they fully understand the large enterprise sales cycle and have numerous relationships that can be readily exploited. The founder and team have deep domain real world experience and have created an offering that is light touch and non-threating to organizations. While the market is vast for this offering management is leveraging a cross-industry association of construction firms and owner / operators which exposes the offering to multiple organizations and creates add on network effects.",
      "Ed Barrows retired as an Executive Partner with Gartner Executive Programs in 2016. Prior to Gartner, Mr. Barrows has operated as a CIO for over 25 years and was the Application Executive covering all business lines worldwide for Occidental Petroleum Corporation (Oxy). Previously, he was the VP and CIO of OxyChem, the commodity chemical arm of Oxy."
    ],
    credentials: []
  }
];

export const InvestorLetterPopup = ({ letters, triggerElement }: InvestorLetterPopupProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? letters.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === letters.length - 1 ? 0 : prev + 1));
  };

  const currentLetter = letters[currentIndex];

  return (
    <>
      <div className="cursor-pointer" onClick={() => setIsOpen(true)}>
        {triggerElement}
      </div>

      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="relative z-10 animate-scale-in bg-white rounded-lg shadow-2xl overflow-hidden flex flex-col"
            style={{
              width: "min(90vw, 700px)",
              height: "min(90vh, 800px)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div>
                <h3 className="font-semibold text-lg text-gray-900">{currentLetter.author}</h3>
                <p className="text-sm text-gray-600">{currentLetter.title}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Letter Content */}
            <div className="flex-1 overflow-y-auto px-8 py-6">
              <div className="max-w-none prose prose-gray">
                <p className="text-gray-700 font-medium mb-4">{currentLetter.greeting}</p>
                
                {currentLetter.opening && (
                  <p className="text-gray-800 font-semibold mb-4">{currentLetter.opening}</p>
                )}

                {currentLetter.paragraphs.map((paragraph, index) => {
                  if (typeof paragraph === "string") {
                    return (
                      <p key={index} className="text-gray-700 leading-relaxed mb-4 text-[15px]">
                        {paragraph}
                      </p>
                    );
                  } else if (paragraph.type === "bullets") {
                    return (
                      <ul key={index} className="list-disc list-inside mb-4 space-y-1 ml-4">
                        {paragraph.items.map((item, bulletIndex) => (
                          <li key={bulletIndex} className="text-gray-700 text-[15px]">
                            {item}
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  return null;
                })}

                {currentLetter.closing && (
                  <div className="mt-8">
                    <p className="text-gray-700 mb-2">{currentLetter.closing}</p>
                    {currentLetter.signature && (
                      <p className="font-semibold text-gray-900 mb-2">{currentLetter.signature}</p>
                    )}
                    {currentLetter.credentials.length > 0 && (
                      <div className="text-sm text-gray-600">
                        {currentLetter.credentials.map((credential, index) => (
                          <p key={index}>{credential}</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Navigation Footer */}
            {letters.length > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToPrevious}
                  className="flex items-center gap-1"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>

                <div className="flex gap-2">
                  {letters.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === currentIndex ? "bg-blue-600" : "bg-gray-300 hover:bg-gray-400"
                      }`}
                    />
                  ))}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={goToNext}
                  className="flex items-center gap-1"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
